import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import styled from 'styled-components';

import { Modal, TextInput, Button, ErrorMessage, useScreenQuery } from 'playup-web-core';

import useOpenModal from '../../../hooks/useOpenModal';
import useNotify from '../../../hooks/useNotify';
import schema from './schema';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';
import { resetPassword } from '../../../api/users';
import ResponsibleGambling from '../ResponsibleGambling';

interface FormData {
	email: string;
	password: string;
}

const ForgotPasswordModal: React.FC<{
	onClose: () => void;
	showRG: boolean;
}> = ({ onClose, showRG = false }) => {
	const { isSmall } = useScreenQuery();
	const notify = useNotify();
	const { user } = React.useContext(PortalSessionContext);
	const handleOpenModal = useOpenModal();

	const [sending, setSending] = React.useState<boolean>(false);
	const [errors, setErrors] = React.useState<any>(null);
	const {
		register,
		handleSubmit,
		formState: { errors: formErrors },
	} = useForm<FormData>({
		resolver: yupResolver(schema),
	});

	if (user?.id) {
		return null;
	}

	const onSubmit = (data: FormData) => {
		setSending(true);

		resetPassword(data.email)
			.then((response) => {
				notify({
					type: 'success',
					title: 'Success',
					message: response.data.message,
					timeout: 5000,
				});
				onClose();
			})
			.catch((error) => {
				setErrors(error.errors);
			})
			.finally(() => setSending(false));
	};

	return (
		<Modal title="Reset Password" onClose={onClose}>
			<form onSubmit={handleSubmit(onSubmit)}>
				<StyledInput
					kind="white"
					type="email"
					label="Email"
					placeholder="Enter a valid Email"
					{...register('email', { required: true })}
					error={errors && errors.email}
				/>
				<Button kind="white" type="submit" height={50} isLoading={sending}>
					Reset Password
				</Button>
				{errors && errors.error && <ErrorMessage>{errors.error}</ErrorMessage>}
				{formErrors && formErrors.email && <ErrorMessage>{formErrors.email.message}</ErrorMessage>}
				<FindAccountSection>
					<InlineText isSmallScreen={isSmall}>Already have an account?</InlineText>
					<LinkButton type="button" onClick={() => handleOpenModal('login')}>
						<UnderlineText isSmallScreen={isSmall}>Login</UnderlineText>
					</LinkButton>
				</FindAccountSection>
			</form>
			{showRG && <ResponsibleGambling />}
		</Modal>
	);
};

export default ForgotPasswordModal;

const StyledInput = styled(TextInput)({
	marginBottom: '30px',
});

const FindAccountSection = styled.div({
	display: 'flex',
	flexDirection: 'row',
	justifyContent: 'center',
	margin: '20px 0',
});

const InlineText = styled.p<{ isSmallScreen: boolean }>(({ theme, isSmallScreen }) => ({
	display: 'inline',
	color: theme.colors.darkR,
	fontSize: isSmallScreen ? '14px' : '18px',
	margin: '5px 0',
}));

const UnderlineText = styled(InlineText)({
	textDecoration: 'underline',
});

const LinkButton = styled.button`
	display: inline;

	&:focus {
		outline: none;
	}
`;
